import { ChangeEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import SignatureCanvas from 'react-signature-canvas';
import { useTranslation } from 'react-i18next';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FolderIcon from '@mui/icons-material/Folder';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import HomeIcon from '@mui/icons-material/Home';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WorkIcon from '@mui/icons-material/Work';
import AddressAutocomplete from './AddressAutocomplete';
import DateField from './DateField';
import FormTextField from './FormTextField';
import FormSection from './FormSection';
import MultiSelectPicklist from './MultiSelectPicklist';
import NavStatusBar from './NavStatusBar';
import Picklist, { PicklistOption } from './Picklist';
import { CaregiverCardIcon, CaregiverFieldConfig, CaregiverFormKind, getSections } from '../config/caregiverForms';
import { isSignatureValid } from '../utils/signatureValidation';
import {
  CaregiverData,
  CaregiverSubmissionStatus,
  GeneratedDocument,
  downloadBlob,
  generateDocuments,
  getCaregiverSubmissionStatus,
  getCaregiver,
  getGeneratedDocumentPdf,
  getSummaryPdf,
  getTemplatePdf,
  listGeneratedDocuments,
  readFileAsDataUrl,
  reportClientEvent,
  saveCaregiver,
  submitCaregiver,
  uploadDocument,
} from '../utils/caregiverApi';

interface CaregiverFormFlowProps {
  kind: CaregiverFormKind;
}

const SUBMISSION_POLL_MAX_ELAPSED_MS = 15 * 60 * 1000;
const SUBMISSION_POLL_MAX_REQUESTS = 60;
const SUBMISSION_POLL_MAX_FAILURES = 5;

function submissionPollDelay(polls: number, workerAttempts: number): number {
  const pollBackoff = Math.max(0, Math.floor((polls - 1) / 5));
  const workerBackoff = Math.max(0, workerAttempts - 1);
  return Math.min(2_000 * (2 ** Math.max(pollBackoff, workerBackoff)), 30_000);
}

function asString(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function asBoolean(value: unknown): boolean {
  return parseBooleanValue(value) === true;
}

function parseBooleanValue(value: unknown): boolean | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value;
  const normalized = String(value).trim().toLowerCase();
  if (['true', 'yes', 'y', '1'].includes(normalized)) return true;
  if (['false', 'no', 'n', '0'].includes(normalized)) return false;
  return null;
}

function asNonNegativeInteger(value: unknown): number {
  const parsed = Number.parseInt(asString(value), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function yesNoValue(value: unknown): string {
  const parsed = parseBooleanValue(value);
  if (parsed === true) return 'Yes';
  if (parsed === false) return 'No';
  return asString(value);
}

function booleanOptionValue(value: unknown): string | null {
  const parsed = parseBooleanValue(value);
  if (parsed === true) return 'Yes';
  if (parsed === false) return 'No';
  return null;
}

function documentLanguageFromPrimaryLanguage(value: unknown): string {
  const normalized = asString(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
  if (normalized === 'spanish' || normalized === 'es') return 'Spanish';
  if (['creole', 'haitiancreole', 'haitian', 'ht', 'kreyol'].includes(normalized)) return 'Creole';
  return 'English';
}

function parseDateOnly(value: unknown): Date | null {
  const stringValue = asString(value).trim();
  if (!stringValue) return null;

  const [datePart] = stringValue.split('T');
  const [year, month, day] = datePart.split('-').map((part) => Number.parseInt(part, 10));
  if (!year || !month || !day) return null;

  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return parsed;
}

function todayDateOnly(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

function dateValue(data: CaregiverData, fieldNames: string[]): Date | null {
  for (const fieldName of fieldNames) {
    const parsed = parseDateOnly(data[fieldName]);
    if (parsed) return parsed;
  }
  return null;
}

function ageAtDate(dateOfBirth: Date, referenceDate: Date): number {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const days = Math.floor((referenceDate.getTime() - dateOfBirth.getTime()) / millisecondsPerDay) + 1;
  return Math.floor(days / 365.2425);
}

function isYouthCreditEligible(data: CaregiverData): boolean {
  const explicitEligibility = parseBooleanValue(data.youthCreditEligibility ?? data.Youth_Credit_Eligibility__c);
  if (explicitEligibility !== null) return explicitEligibility;

  const dateOfBirth = dateValue(data, ['dateOfBirth', 'Birthdate']);
  if (!dateOfBirth) return false;

  const paStartDate = dateValue(data, ['paStartOfWorkDate', 'PA_start_of_work_date__c']);
  const hhaStartDate = dateValue(data, ['hhaStartOfWorkDate', 'HHA_start_of_work_date__c', 'hhaHireDate']);
  const referenceDate = paStartDate && (!hhaStartDate || paStartDate >= hhaStartDate)
    ? paStartDate
    : hhaStartDate ?? todayDateOnly();
  const age = ageAtDate(dateOfBirth, referenceDate);
  return age >= 18 && age < 25;
}

function isPastDate(value: unknown): boolean {
  const parsed = parseDateOnly(value);
  if (!parsed) return false;
  return parsed < todayDateOnly();
}

const US_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'AS', 'GU', 'MP', 'PR', 'VI',
]);
const FORM_ICON_COLOR = '#8600e1';
const TERMINAL_PORTAL_STATUSES = ['App Submitted', 'Review'] as const;
const FEDERAL_FILING_STATUS_OPTIONS = [
  {
    value: 'Married',
    label: 'Married filing jointly or Qualifying surviving spouse',
  },
  {
    value: 'Single or Married filing separately',
    label: 'Single or Married filing separately',
  },
  {
    value: 'Head of household',
    label: "Head of household (Check only if you're unmarried and pay more than half the costs of keeping up a home for yourself and a qualifying individual.)",
  },
];
const NY_FILING_STATUS_OPTIONS = ['Married', 'Single or Married filing separately', 'Head of household'];
const WAIVED_BENEFITS_OPTIONS: PicklistOption[] = [
  {
    value: 'Yes',
    label: 'I choose to waive medical benefits (no payroll deduction)',
  },
  {
    value: 'No',
    label: 'I would like to enroll in medical benefits and understand the cost will be deducted from my paycheck',
  },
];
const YES_NO_OPTIONS = ['Yes', 'No'];
const TAX_AMOUNT_MASK = '999999999999999999';
const TAX_ALLOWANCE_MASK = '99';
const REGION_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AL', 'AM', 'AO', 'AR', 'AT', 'AU', 'AZ', 'BA', 'BB', 'BD',
  'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BN', 'BO', 'BR', 'BS', 'BT', 'BW', 'BY', 'BZ',
  'CA', 'CD', 'CF', 'CG', 'CH', 'CI', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CY',
  'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE', 'EG', 'ER', 'ES', 'ET', 'FI',
  'FJ', 'FM', 'FR', 'GA', 'GB', 'GD', 'GE', 'GH', 'GM', 'GN', 'GQ', 'GR', 'GT', 'GW',
  'GY', 'HK', 'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IN', 'IQ', 'IR', 'IS', 'IT',
  'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KZ', 'LA',
  'LB', 'LC', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MG', 'MH',
  'MK', 'ML', 'MM', 'MN', 'MO', 'MR', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
  'NE', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NZ', 'OM', 'PA', 'PE', 'PG', 'PH', 'PK',
  'PL', 'PS', 'PT', 'PW', 'PY', 'QA', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC', 'SD',
  'SE', 'SG', 'SI', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SY', 'SZ',
  'TD', 'TG', 'TH', 'TJ', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ', 'UA',
  'UG', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VN', 'VU', 'WS', 'YE', 'ZA', 'ZM', 'ZW',
  'XK',
];
const REGION_ALIASES: Record<string, string> = {
  unitedstatesofamerica: 'US',
  unitedkingdom: 'GB',
  russianfederation: 'RU',
  vietnam: 'VN',
  macao: 'MO',
  macedonia: 'MK',
  palestinianterritories: 'PS',
  congo: 'CG',
  congodemrep: 'CD',
  cotedivoireivorycoast: 'CI',
  stvincentandthegrenadines: 'VC',
  saintkittsandnevis: 'KN',
  saintlucia: 'LC',
  brunei: 'BN',
  capeverde: 'CV',
  czechrepublic: 'CZ',
  eswatini: 'SZ',
  hongkong: 'HK',
  kosovo: 'XK',
  southkorea: 'KR',
  northkorea: 'KP',
  timorleste: 'TL',
  vaticancity: 'VA',
};

interface WotcBullet {
  key: string;
  fallback: string;
  children?: WotcBullet[];
}

interface WotcQuestion {
  fieldName: string;
  key: string;
  fallback: string;
  bullets?: WotcBullet[];
}

const WOTC_QUESTIONS: WotcQuestion[] = [
  {
    fieldName: 'wotcQuestion1',
    key: 'WOTC_Question1__c',
    fallback: 'Have you received a conditional certification from the state workforce agency (SWA) or a participating local agency for the work opportunity credit?',
  },
  {
    fieldName: 'wotcQuestion2',
    key: 'WOTC_Question2__c',
    fallback: 'Select Yes, if any of the following statements apply to you. Otherwise select No.',
    bullets: [
      {
        key: 'WOTC_Question2__cli1',
        fallback: 'I am a member of a family that has received assistance from Temporary Assistance for Needy Families (TANF) for any 9 months during the past 18 months.',
      },
      {
        key: 'WOTC_Question2__cli2',
        fallback: 'I am a veteran and a member of a family that received Supplemental Nutrition Assistance Program (SNAP) benefits (food stamps) for at least a 3-month period during the past 15 months.',
      },
      {
        key: 'WOTC_Question2__cli3',
        fallback: 'I was referred here by a rehabilitation agency approved by the state, an employment network under the Ticket to Work program, or the Department of Veterans Affairs.',
      },
      {
        key: 'WOTC_Question2__cli4',
        fallback: 'I am at least age 18 but not age 40 or older and I am a member of a family that:',
        children: [
          {
            key: 'WOTC_Question2__cli4a',
            fallback: 'a. Received SNAP benefits (food stamps) for the past 6 months; or',
          },
          {
            key: 'WOTC_Question2__cli4b',
            fallback: 'b. Received SNAP benefits (food stamps) for at least 3 of the past 5 months, but is no longer eligible to receive them.',
          },
        ],
      },
      {
        key: 'WOTC_Question2__cli5',
        fallback: 'During the past year, I was convicted of a felony or released from prison for a felony.',
      },
      {
        key: 'WOTC_Question2__cli6',
        fallback: 'I received supplemental security income (SSI) benefits for any month ending during the past 60 days.',
      },
      {
        key: 'WOTC_Question2__cli7',
        fallback: 'I am a veteran and I was unemployed for a period or periods totaling at least 4 weeks but less than 6 months during the past year.',
      },
    ],
  },
  {
    fieldName: 'wotcQuestion3',
    key: 'WOTC_Question3__c',
    fallback: 'Are you a veteran and you were unemployed for a period or periods totaling at least 6 months during the past year?',
  },
  {
    fieldName: 'wotcQuestion4',
    key: 'WOTC_Question4__c',
    fallback: 'Are you a veteran entitled to compensation for a service-connected disability and you were discharged or released from active duty in the U.S. Armed Forces during the past year. released from active duty in the U.S. Armed Forces during the past year?',
  },
  {
    fieldName: 'wotcQuestion5',
    key: 'WOTC_Question5__c',
    fallback: 'Check yes here if you are a veteran entitled to compensation for a service-connected disability and you were unemployed for a period or periods totaling at least 6 months during the past year.',
  },
  {
    fieldName: 'wotcQuestion6',
    key: 'WOTC_Question6__c',
    fallback: 'Check yes here if you are a member of a family that:',
    bullets: [
      {
        key: 'WOTC_Question6__cli1',
        fallback: 'Received TANF payments for at least the past 18 months; or',
      },
      {
        key: 'WOTC_Question6__cli2',
        fallback: 'Received TANF payments for any 18 months beginning after August 5, 1997, and the earliest 18-month period beginning after August 5, 1997, ended during the past 2 years; or',
      },
      {
        key: 'WOTC_Question6__cli3',
        fallback: 'Stopped being eligible for TANF payments during the past 2 years because federal or state law limited the maximum time those payments could be made.',
      },
    ],
  },
  {
    fieldName: 'wotcQuestion7',
    key: 'WOTC_Question7__c',
    fallback: 'Check yes here if you are in a period of unemployment that is at least 27 consecutive weeks and for all or part of that period you received unemployment compensation.',
  },
];

const YOUTH_CREDIT_QUESTIONS: WotcQuestion[] = [
  {
    fieldName: 'youthQuestion1',
    key: 'Youth_Question1__c',
    fallback: 'Check yes here if you have a high school diploma, a GED or HSE diploma, satisfactorily completed a TASC exam, or enrolled in a TASC Program.',
  },
  {
    fieldName: 'youthQuestion2',
    key: 'Youth_Question2__c',
    fallback: 'I was unemployed prior to being hired or I do not have enough paid work.',
  },
  {
    fieldName: 'youthQuestion3',
    key: 'Youth_Question3__c',
    fallback: 'I confirm that I currently meet one or more of the youth categories listed below:',
    bullets: [
      {
        key: 'Youth_Question3__cli1',
        fallback: 'I am over 18 and do not have a high school diploma of GED/HSE diploma.',
      },
      {
        key: 'Youth_Question3__cli2',
        fallback: 'I am a member of a family that is receiving assistance from TANF.',
      },
      {
        key: 'Youth_Question3__cli3',
        fallback: 'I am a member of a family that is receiving SNAP benefits (food stamps).',
      },
      {
        key: 'Youth_Question3__cli4',
        fallback: 'I am a member of a family that is receiving SSI benefits.',
      },
      {
        key: 'Youth_Question3__cli5',
        fallback: 'I am receiving a free or reduced-cost school lunch.',
      },
      {
        key: 'Youth_Question3__cli6',
        fallback: 'I have served in jail or prison, or am on probation or parole.',
      },
      {
        key: 'Youth_Question3__cli7',
        fallback: 'I am pregnant or a parent.',
      },
      {
        key: 'Youth_Question3__cli8',
        fallback: 'I am currently or was in foster care of the custody of the Office of Children and Family Services.',
      },
      {
        key: 'Youth_Question3__cli9',
        fallback: 'I am a veteran.',
      },
      {
        key: 'Youth_Question3__cli10',
        fallback: 'I am the daughter or son of a parent who is currently in jail or prison, or has been within the past two years.',
      },
      {
        key: 'Youth_Question3__cli11',
        fallback: 'I am the daughter or son of a parent who is collecting unemployment insurance.',
      },
      {
        key: 'Youth_Question3__cli12',
        fallback: 'I live in public housing or receive housing assistance such as a Section 8 voucher, or am homeless.',
      },
      {
        key: 'Youth_Question3__cli13',
        fallback: 'Another risk factor not identified above',
      },
    ],
  },
];

const TB_RISK_QUESTIONS: WotcQuestion[] = Array.from({ length: 5 }, (_, index) => ({
  fieldName: `tbRiskQuestion${index + 1}`,
  key: `TB_Risk_Question_${index + 1}__c`,
  fallback: `TB Risk Question ${index + 1}`,
}));

const TB_SYMPTOM_QUESTIONS: WotcQuestion[] = Array.from({ length: 9 }, (_, index) => ({
  fieldName: `tbSymptomsQuestion${index + 1}`,
  key: `TB_Symptoms_Question_${index + 1}__c`,
  fallback: `TB Symptoms Question ${index + 1}`,
}));

const TB_NEEDS_REVIEW_FIELDS = [
  'tbRiskQuestion3',
  'tbRiskQuestion5',
  ...TB_SYMPTOM_QUESTIONS.map((question) => question.fieldName),
];

function fileLabel(data: CaregiverData, fieldName: string): string {
  return asBoolean(data[fieldName]) ? 'Uploaded' : 'No file uploaded';
}

function inputMaskFor(field: CaregiverFieldConfig): string | null {
  if (field.type === 'tel' || field.name.toLowerCase().includes('phone')) return '999 999 9999';
  if (field.name === 'ssn') return '999-99-9999';
  if (field.name === 'zipCode') return '99999';
  if (field.name === 'abaRoutingNumber') return '999999999';
  return null;
}

function digitsOnly(value: unknown): string {
  return asString(value).replace(/\D/g, '');
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isCensoredSsn(value: string): boolean {
  return /^\*{5}\d{4}$/.test(value.replace(/[^*\d]/g, ''));
}

function hasUnsupportedCharacters(value: string): boolean {
  return !/^[\x20-\x7E\xA0-\xFF\r\n]*$/.test(value);
}

function hasNoneOfAboveConflict(value: unknown): boolean {
  const values = splitMultiValue(value);
  return values.includes('None of the above') && values.length > 1;
}

function isAvailabilityField(fieldName: string): boolean {
  return [
    'sundayAvailability',
    'mondayAvailability',
    'tuesdayAvailability',
    'wednesdayAvailability',
    'thursdayAvailability',
    'fridayAvailability',
    'saturdayAvailability',
  ].includes(fieldName);
}

function splitMultiValue(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  return asString(value).split(';').map((item) => item.trim()).filter(Boolean);
}

function optionKey(value: string): string {
  return value.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

function localizedRegionName(value: string, locale: string): string | null {
  if (locale === 'en' || typeof Intl === 'undefined' || typeof Intl.DisplayNames === 'undefined') {
    return null;
  }

  const normalized = optionKey(value);
  const aliasCode = REGION_ALIASES[normalized];
  const englishRegions = new Intl.DisplayNames(['en'], { type: 'region' });
  const code = aliasCode ?? REGION_CODES.find((regionCode) => (
    optionKey(englishRegions.of(regionCode) || '') === normalized
  ));
  if (!code) return null;

  return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? null;
}

function normalizeOptionValue(value: unknown, options: string[] = []): string {
  const stringValue = asString(value);
  if (!stringValue) return '';
  if (options.includes(stringValue)) return stringValue;
  return options.find((option) => optionKey(option) === optionKey(stringValue)) ?? humanizeApiValue(stringValue);
}

function humanizeApiValue(value: string): string {
  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => (['and', 'of', 'the', 'as'].includes(word) ? word : `${word[0].toUpperCase()}${word.slice(1)}`))
    .join(' ');
}

function normalizeMultiValues(value: unknown, options: string[] = []): string[] {
  return Array.from(new Set(splitMultiValue(value).map((item) => normalizeOptionValue(item, options))));
}

function selectOptionsForField(field: CaregiverFieldConfig): PicklistOption[] {
  if (field.name === 'waivedBenefits') return WAIVED_BENEFITS_OPTIONS;
  if (field.selectOptions) return field.selectOptions.map((option) => ({ ...option }));
  return (field.options ?? []).map((option) => ({ value: option, label: option }));
}

function normalizeSelectOptionValue(value: unknown, options: PicklistOption[]): string {
  const booleanValue = booleanOptionValue(value);
  if (booleanValue) {
    const matchedBooleanOption = options.find((option) => optionKey(option.value) === optionKey(booleanValue));
    if (matchedBooleanOption) return matchedBooleanOption.value;
  }

  const stringValue = asString(value);
  if (!stringValue) return '';
  const matched = options.find((option) => (
    optionKey(option.value) === optionKey(stringValue)
    || optionKey(option.label) === optionKey(stringValue)
  ));
  return matched?.value ?? stringValue;
}

function isFluOtherReasonSelected(value: unknown): boolean {
  return normalizeMultiValues(value).some((item) => optionKey(item).startsWith('other'));
}

const ALLERGY_FIELD_VALUE: Record<string, string> = {
  allergyCat: 'Cats',
  allergyDog: 'Dogs',
  allergyOtherPets: 'Other',
  allergySmoking: 'Smoke',
};

type AllergySelections = Record<string, string>;

function emptyAllergySelections(): AllergySelections {
  return Object.fromEntries(Object.keys(ALLERGY_FIELD_VALUE).map((fieldName) => [fieldName, ''])) as AllergySelections;
}

function parseAllergySelections(value: unknown): AllergySelections {
  const selections = emptyAllergySelections();
  const allergyOptions = [...Object.values(ALLERGY_FIELD_VALUE), 'None of the above'];
  const values = splitMultiValue(value).map((item) => normalizeOptionValue(item, allergyOptions));
  if (values.length === 0) return selections;
  if (values.includes('None of the above')) {
    Object.keys(ALLERGY_FIELD_VALUE).forEach((fieldName) => {
      selections[fieldName] = 'No';
    });
    return selections;
  }

  Object.entries(ALLERGY_FIELD_VALUE).forEach(([fieldName, allergyValue]) => {
    selections[fieldName] = values.includes(allergyValue) ? 'Yes' : 'No';
  });
  return selections;
}

function fieldPayloadValue(field: CaregiverFieldConfig, data: CaregiverData): string | boolean {
  if (field.type === 'checkbox') return asBoolean(data[field.name]);
  if (field.type === 'multiselect') return normalizeMultiValues(data[field.name], field.options ?? []).join(';');
  if (field.type === 'select') return normalizeSelectOptionValue(data[field.name], selectOptionsForField(field));
  return asString(data[field.name]);
}

function composeAllergySelections(selections: AllergySelections): string {
  const selected = Object.entries(ALLERGY_FIELD_VALUE)
    .filter(([key]) => selections[key] === 'Yes')
    .map(([, allergyValue]) => allergyValue);

  if (selected.length > 0) return selected.join(';');
  const allAnswered = Object.keys(ALLERGY_FIELD_VALUE).every((key) => selections[key] === 'Yes' || selections[key] === 'No');
  return allAnswered ? 'None of the above' : '';
}

function displayFieldValue(field: CaregiverFieldConfig, data: CaregiverData): string {
  if (field.type === 'checkbox') return asBoolean(data[field.name]) ? 'Yes' : 'No';
  const value = asString(data[field.name]).trim();
  if (!value) return '';
  if (field.type === 'date') {
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
  }
  return field.type === 'multiselect' ? value.split(';').filter(Boolean).join(', ') : value;
}

function isFieldVisible(field: CaregiverFieldConfig, data: CaregiverData): boolean {
  const directDepositFields = new Set([
    'financialInstitution',
    'bankAccountNumber',
    'abaRoutingNumber',
    'bankAccountReEntry',
    'bankAccountType',
  ]);
  if (directDepositFields.has(field.name)) {
    return normalizeOptionValue(data.paymentMethod, ['Direct Deposit']) === 'Direct Deposit';
  }
  if (field.name === 'fluDeclineOtherReason') {
    return isFluOtherReasonSelected(data.fluDeclinationReason);
  }
  return true;
}

function iconFor(icon?: CaregiverCardIcon) {
  switch (icon) {
    case 'accountBalance':
      return <AccountBalanceIcon />;
    case 'assignment':
      return <AssignmentIndIcon />;
    case 'calendar':
      return <CalendarMonthIcon />;
    case 'contact':
      return <ContactEmergencyIcon />;
    case 'description':
      return <DescriptionIcon />;
    case 'favorite':
      return <FavoriteIcon />;
    case 'folder':
      return <FolderIcon />;
    case 'health':
      return <HealthAndSafetyIcon />;
    case 'home':
      return <HomeIcon />;
    case 'payment':
      return <PaymentIcon />;
    case 'person':
      return <PersonIcon />;
    case 'signature':
      return <BorderColorIcon />;
    case 'work':
      return <WorkIcon />;
    default:
      return undefined;
  }
}

export default function CaregiverFormFlow({ kind }: CaregiverFormFlowProps) {
  const { i18n, t } = useTranslation();
  const [formData, setFormData] = useState<CaregiverData>({});
  const includeYouthCredit = kind === 'onboarding' && isYouthCreditEligible(formData);
  const sections = useMemo(() => getSections(kind, includeYouthCredit), [kind, includeYouthCredit]);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentWotcStep, setCurrentWotcStep] = useState(1);
  const [currentYouthCreditStep, setCurrentYouthCreditStep] = useState(1);
  const [currentTbStep, setCurrentTbStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [allergySelections, setAllergySelections] = useState<AllergySelections>(() => emptyAllergySelections());
  const [templateDialog, setTemplateDialog] = useState<{
    title: string;
    templateName: string;
    language: string;
    blob: Blob | null;
    url: string | null;
    loading: boolean;
    error: string | null;
  } | null>(null);
  const [generatedDocs, setGeneratedDocs] = useState<GeneratedDocument[]>([]);
  const [submissionProcessing, setSubmissionProcessing] = useState<CaregiverSubmissionStatus | null>(null);
  const [generatedDocDialog, setGeneratedDocDialog] = useState<{
    document: GeneratedDocument;
    url: string | null;
    loading: boolean;
    error: string | null;
  } | null>(null);
  const [initialPortalStatus, setInitialPortalStatus] = useState<string | null>(null);
  const signaturePadRef = useRef<SignatureCanvas | null>(null);
  const applicantInitialsInputRef = useRef<HTMLInputElement | null>(null);
  const appliedInitialStepRef = useRef(false);

  const currentSection = sections[currentStep];
  const signatureFieldName = kind === 'application' ? 'applicationSignature' : 'onboardingSignature';
  const signatureSavedFieldName = kind === 'application' ? 'hasApplicationSignature' : 'hasOnboardingSignature';
  const signatureSaved = kind === 'application'
    ? asBoolean(formData.hasApplicationSignature) || Boolean(asString(formData.applicationSignature))
    : asBoolean(formData.hasOnboardingSignature) || Boolean(asString(formData.onboardingSignature));
  const currentFields = currentSection.cards.flatMap((card) => card.fields).filter((field) => isFieldVisible(field, formData));
  const hasSignatureField = currentFields.some((field) => field.type === 'signature');
  const isApplicationSubmitStep = kind === 'application' && currentSection.status === 'App Consent';
  const isOnboardingSubmitStep = kind === 'onboarding' && currentSection.status === 'I-9';
  const isOnboardingTaxStep = kind === 'onboarding' && currentSection.status === 'Tax Forms';
  const isOnboardingWotcStep = kind === 'onboarding' && currentSection.status === 'WOTC';
  const isOnboardingYouthCreditStep = kind === 'onboarding' && currentSection.status === 'Youth Credit';
  const isOnboardingPreferencesStep = kind === 'onboarding' && currentSection.status === 'Preferences';
  const isOnboardingTbStep = kind === 'onboarding' && currentSection.status === 'TB Assessment';
  const isOnboardingI9Step = kind === 'onboarding' && currentSection.status === 'I-9';
  const showFluDeclination = isPastDate(formData.fluExpiration);
  const terminalPortalStatus = kind === 'application' ? 'App Submitted' : 'Review';
  const currentPortalStatusKey = optionKey(asString(formData.portalStatus));
  const isWorkflowLocked = TERMINAL_PORTAL_STATUSES.some(
    (status) => currentPortalStatusKey === optionKey(status),
  );
  const isSubmittedStatus = isWorkflowLocked || currentSection.status === terminalPortalStatus;
  const isFinalEditableStep = isApplicationSubmitStep || isOnboardingSubmitStep || currentStep === sections.length - 1;
  const translateText = useCallback((value: string) => {
    const translated = t(value, value);
    if (translated !== value) return translated;
    return localizedRegionName(value, i18n.language) ?? value;
  }, [i18n.language, t]);
  const translateOptionalText = useCallback((value?: string) => (value ? translateText(value) : value), [translateText]);
  const fieldLabel = useCallback((field: CaregiverFieldConfig) => {
    const tbRiskMatch = field.name.match(/^tbRiskQuestion(\d+)$/);
    if (tbRiskMatch) {
      return t(`TB_Risk_Question_${tbRiskMatch[1]}__c`, field.label);
    }

    const tbSymptomsMatch = field.name.match(/^tbSymptomsQuestion(\d+)$/);
    if (tbSymptomsMatch) {
      return t(`TB_Symptoms_Question_${tbSymptomsMatch[1]}__c`, field.label);
    }

    return translateText(field.label);
  }, [t, translateText]);
  const helperText = useCallback((field: CaregiverFieldConfig) => translateOptionalText(field.helperText), [translateOptionalText]);
  const translatedSections = useMemo(
    () => sections.map((section) => ({ status: section.status, title: translateText(section.title) })),
    [sections, translateText]
  );
  const documentLanguage = documentLanguageFromPrimaryLanguage(formData.primaryLanguage);
  const documentLanguageLabel = translateText(documentLanguage);

  const submitDebugDetails = useCallback((extra: Record<string, unknown> = {}) => ({
    kind,
    currentStep,
    currentStatus: currentSection.status,
    isFinalEditableStep,
    isApplicationSubmitStep,
    isOnboardingSubmitStep,
    signatureFieldName,
    signatureSaved,
    hasSignatureValue: Boolean(asString(formData[signatureFieldName])),
    portalStatus: formData.portalStatus,
    nexusId: formData.nexusId,
    contactPhotoUrl: formData.contactPhotoUrl,
    idPictureDocUploaded: formData.idPictureDocUploaded,
    showFluDeclination,
    ...extra,
  }), [
    currentSection.status,
    currentStep,
    formData,
    isApplicationSubmitStep,
    isFinalEditableStep,
    isOnboardingSubmitStep,
    kind,
    showFluDeclination,
    signatureFieldName,
    signatureSaved,
  ]);

  const translateDelimitedValue = useCallback((value: string, options: PicklistOption[] = []): string => {
    const translateSingleValue = (item: string) => {
      const trimmed = item.trim();
      const matchedOption = options.find((option) => (
        option.value === trimmed
        || option.label === trimmed
        || optionKey(option.value) === optionKey(trimmed)
        || optionKey(option.label) === optionKey(trimmed)
      ));
      return translateText(matchedOption?.label ?? trimmed);
    };

    const parts = value.split(/[;,]/).map((item) => item.trim()).filter(Boolean);
    if (parts.length <= 1) return translateSingleValue(value);
    return parts.map(translateSingleValue).join(', ');
  }, [translateText]);

  const translatedFieldValue = useCallback((field: CaregiverFieldConfig): string => {
    const booleanValue = booleanOptionValue(formData[field.name]);
    if ((field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && booleanValue) {
      return translateText(booleanValue);
    }

    const value = displayFieldValue(field, formData);
    if (!value) return '';
    if (field.type === 'select' || field.type === 'radio' || field.type === 'multiselect' || field.type === 'checkbox') {
      const selectOptions = field.type === 'select'
        ? selectOptionsForField(field)
        : (field.options ?? []).map((option) => ({ value: option, label: option }));
      return translateDelimitedValue(value, selectOptions);
    }
    if (['Yes', 'No', 'Uploaded', 'No file uploaded', 'Completed', 'None of the above'].includes(value)) {
      return translateText(value);
    }
    return value;
  }, [formData, translateDelimitedValue, translateText]);

  const adjustSignaturePadSize = useCallback(() => {
    if (signaturePadRef.current && (signaturePadRef.current as any).getCanvas) {
      const canvas = (signaturePadRef.current as any).getCanvas() as HTMLCanvasElement;
      const container = canvas.parentNode as HTMLElement;
      if (!container) return;
      const { width } = container.getBoundingClientRect();

      canvas.width = width;
      canvas.style.width = `${width}px`;
      canvas.height = 200;
      canvas.style.height = '200px';
    }
  }, []);

  useLayoutEffect(() => {
    if (hasSignatureField) adjustSignaturePadSize();
  }, [adjustSignaturePadSize, currentStep, hasSignatureField]);

  useEffect(() => {
    if (!hasSignatureField) return undefined;
    const timeout = window.setTimeout(adjustSignaturePadSize, 100);
    window.addEventListener('resize', adjustSignaturePadSize);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('resize', adjustSignaturePadSize);
    };
  }, [adjustSignaturePadSize, currentStep, hasSignatureField]);

  useEffect(() => {
    if (!isApplicationSubmitStep && !isOnboardingI9Step) return undefined;

    let touchStartY: number | null = null;
    const blurInitials = () => {
      const input = applicantInitialsInputRef.current;
      if (input && document.activeElement === input) input.blur();
    };
    const handleTouchStart = (event: TouchEvent) => {
      touchStartY = event.touches[0]?.clientY ?? null;
    };
    const handleTouchMove = (event: TouchEvent) => {
      const currentY = event.touches[0]?.clientY;
      if (touchStartY !== null && currentY !== undefined && touchStartY - currentY > 8) {
        blurInitials();
        touchStartY = currentY;
      }
    };
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isApplicationSubmitStep, isOnboardingI9Step]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const caregiver = await getCaregiver();
        if (cancelled) return;
        setFormData(caregiver);
        setAllergySelections(parseAllergySelections(caregiver.allergiesAfraidOf));
        setInitialPortalStatus(asString(caregiver.portalStatus));
        appliedInitialStepRef.current = false;
      } catch (loadError) {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : t('caregiver.loadFailed', 'Failed to load form'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [kind]);

  useEffect(() => {
    if (!initialPortalStatus || appliedInitialStepRef.current) return;

    const normalizedStatus = normalizeOptionValue(initialPortalStatus, sections.map((section) => section.status));
    const statusIndex = sections.findIndex((section) => section.status === normalizedStatus);
    if (statusIndex >= 0) setCurrentStep(statusIndex);
    appliedInitialStepRef.current = true;
  }, [initialPortalStatus, sections]);

  useEffect(() => {
    if (!isWorkflowLocked) return;
    const terminalStep = sections.findIndex((section) => section.status === terminalPortalStatus);
    if (terminalStep >= 0 && currentStep !== terminalStep) {
      setCurrentStep(terminalStep);
    }
  }, [currentStep, isWorkflowLocked, sections, terminalPortalStatus]);

  useEffect(() => {
    return () => {
      if (templateDialog?.url) URL.revokeObjectURL(templateDialog.url);
    };
  }, [templateDialog?.url]);

  useEffect(() => () => {
    if (generatedDocDialog?.url) URL.revokeObjectURL(generatedDocDialog.url);
  }, [generatedDocDialog?.url]);

  useEffect(() => {
    let cancelled = false;
    let refreshTimer: number | undefined;
    let polls = 0;
    let consecutiveFailures = 0;
    const startedAt = Date.now();
    const canContinuePolling = () => (
      polls < SUBMISSION_POLL_MAX_REQUESTS
      && Date.now() - startedAt < SUBMISSION_POLL_MAX_ELAPSED_MS
    );
    const schedulePoll = (delay: number) => {
      if (!cancelled && canContinuePolling()) {
        refreshTimer = window.setTimeout(() => void loadSubmissionProgress(), delay);
      }
    };
    async function loadSubmissionProgress() {
      if (!isSubmittedStatus || !canContinuePolling()) return;
      polls += 1;
      try {
        const [documents, submission] = await Promise.all([
          listGeneratedDocuments(kind === 'application' ? 'application' : 'onboarding'),
          getCaregiverSubmissionStatus(kind),
        ]);
        if (cancelled) return;
        setGeneratedDocs(documents);
        setSubmissionProcessing(submission);
        consecutiveFailures = 0;
        setError(null);
        if (
          submission.status === 'pending'
          || submission.status === 'processing'
          || (submission.status === 'completed' && documents.length < submission.documentCount)
        ) {
          schedulePoll(submissionPollDelay(polls, submission.attempts));
        }
      } catch (docsError) {
        if (cancelled) return;
        consecutiveFailures += 1;
        setError(docsError instanceof Error ? docsError.message : t('documents.loadGeneratedFailed', 'Failed to load generated documents'));
        if (consecutiveFailures < SUBMISSION_POLL_MAX_FAILURES) {
          schedulePoll(Math.min(5_000 * (2 ** (consecutiveFailures - 1)), 30_000));
        }
      }
    }
    void loadSubmissionProgress();
    return () => {
      cancelled = true;
      if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
    };
  }, [kind, currentSection.status, isSubmittedStatus]);

  const updateValue = (name: string, value: string | boolean) => {
    setFormData((previous) => ({ ...previous, [name]: value }));
    setFieldErrors((previous) => {
      if (!previous[name]) return previous;
      const next = { ...previous };
      delete next[name];
      return next;
    });
    setError(null);
  };

  const updateAddress = ({ street, aptSuite, city, state, zipCode }: { street: string; aptSuite: string; city: string; state: string; zipCode: string }) => {
    setFormData((previous) => ({
      ...previous,
      street,
      aptSuite,
      city,
      state,
      zipCode,
    }));
    setFieldErrors((previous) => {
      const next = { ...previous };
      delete next.street;
      delete next.aptSuite;
      delete next.city;
      delete next.state;
      delete next.zipCode;
      return next;
    });
    setError(null);
  };

  const updateDependentCounts = (fieldName: 'dependentsUnder17' | 'otherDependents', value: string) => {
    const nextValue = asNonNegativeInteger(value);
    setFormData((previous) => {
      const dependentsUnder17 = fieldName === 'dependentsUnder17'
        ? nextValue
        : asNonNegativeInteger(previous.dependentsUnder17);
      const otherDependents = fieldName === 'otherDependents'
        ? nextValue
        : asNonNegativeInteger(previous.otherDependents);
      const dependentCredit = (dependentsUnder17 * 2200) + (otherDependents * 500);

      return {
        ...previous,
        dependentsUnder17,
        otherDependents,
        dependentCredit: dependentCredit > 0 ? String(dependentCredit) : '',
      };
    });
    setError(null);
  };

  const validateCurrentFields = () => {
    const nextFieldErrors: Record<string, string> = {};
    let nextSignatureError: string | null = null;
    const currentFieldNames = new Set(currentFields.map((field) => field.name));
    const hasAvailabilityFields = currentFields.some((field) => isAvailabilityField(field.name));

    currentFields.forEach((field) => {
      const rawValue = asString(formData[field.name]).trim();

      if (!field.required) return;

      const label = fieldLabel(field);
      const message = t('FieldRequiredError', { field: label, defaultValue: `${label} is required` });
      if (field.type === 'signature') {
        if (!signatureSaved) nextSignatureError = message;
        return;
      }
      if (field.type === 'heading') return;
      if (field.type === 'radio' && ALLERGY_FIELD_VALUE[field.name]) {
        if (!allergySelections[field.name]) nextFieldErrors[field.name] = message;
        return;
      }
      if (!rawValue) {
        nextFieldErrors[field.name] = message;
      }
    });

    currentFields.forEach((field) => {
      if (nextFieldErrors[field.name]) return;

      const rawValue = formData[field.name];
      if (field.type === 'multiselect') {
        const serializedValue = normalizeMultiValues(rawValue, field.options ?? []).join(';');
        if (serializedValue.length > 255) {
          nextFieldErrors[field.name] = t('LongValueError', 'Value is too long');
          return;
        }
        if (hasNoneOfAboveConflict(rawValue)) {
          nextFieldErrors[field.name] = t('NoneOfTheAboveError', 'Please select either None of the above or another option, not both');
        }
        return;
      }

      const value = asString(rawValue).trim();
      if (!value) return;
      const validatesTextContent = !['checkbox', 'file', 'photo', 'signature', 'summary', 'heading'].includes(field.type ?? 'text');

      if (field.type === 'email' && !isValidEmail(value)) {
        nextFieldErrors[field.name] = t('EmailError', 'Please enter a valid email address');
      } else if ((field.type === 'tel' || field.name.toLowerCase().includes('phone')) && digitsOnly(value).length !== 10) {
        nextFieldErrors[field.name] = t('PhoneError', 'Please enter a valid 10 digit phone number');
      } else if (field.name === 'zipCode' && digitsOnly(value).length !== 5) {
        nextFieldErrors[field.name] = t('ZipError', 'Please enter a valid 5 digit zip code');
      } else if (field.name === 'state') {
        const stateValue = value.toUpperCase();
        if (!US_STATE_CODES.has(stateValue)) {
          nextFieldErrors[field.name] = t('StateError', 'Please enter a valid two-letter state code');
        }
      } else if (field.name === 'ssn' && !isCensoredSsn(value) && digitsOnly(value).length !== 9) {
        nextFieldErrors[field.name] = t('SSNError', 'Please enter a valid 9 digit SSN');
      } else if (field.name === 'abaRoutingNumber' && digitsOnly(value).length !== 9) {
        nextFieldErrors[field.name] = t('RoutingNumberError', 'Please enter a valid 9 digit routing number');
      } else if (field.name === 'bankAccountNumber' && value.length > 20) {
        nextFieldErrors[field.name] = t('BankAccountNumberError', 'Bank account number must be 20 characters or fewer');
      } else if (validatesTextContent && value.length > 255) {
        nextFieldErrors[field.name] = t('LongValueError', 'Value is too long');
      } else if (validatesTextContent && hasUnsupportedCharacters(value)) {
        nextFieldErrors[field.name] = t('InvalidCharactersError', 'Please remove unsupported characters');
      }
    });

    if (hasAvailabilityFields) {
      const availabilityFields = currentFields.filter((field) => isAvailabilityField(field.name));
      const hasAnyAvailability = availabilityFields.some((field) => asString(formData[field.name]).trim());
      if (!hasAnyAvailability) {
        const message = t('AvailabilityRequiredError', 'Please select at least one availability option');
        availabilityFields.forEach((field) => {
          nextFieldErrors[field.name] = message;
        });
      }
    }

    if (
      currentFieldNames.has('bankAccountNumber')
      && currentFieldNames.has('bankAccountReEntry')
      && asString(formData.bankAccountNumber).trim()
      && asString(formData.bankAccountReEntry).trim()
      && asString(formData.bankAccountNumber).trim() !== asString(formData.bankAccountReEntry).trim()
    ) {
      const message = t('BankAccountNoMatchError', 'Bank account numbers do not match');
      nextFieldErrors.bankAccountNumber = message;
      nextFieldErrors.bankAccountReEntry = message;
    }

    if (isOnboardingI9Step && showFluDeclination && !asString(formData.fluDeclinationReason).trim()) {
      nextFieldErrors.fluDeclinationReason = t('RequiredFieldError', 'This field is required');
    }
    if (
      isOnboardingI9Step
      && isFluOtherReasonSelected(formData.fluDeclinationReason)
      && !asString(formData.fluDeclineOtherReason).trim()
    ) {
      nextFieldErrors.fluDeclineOtherReason = t('RequiredFieldError', 'This field is required');
    }

    setFieldErrors(nextFieldErrors);
    setSignatureError(nextSignatureError);
    return {
      isValid: Object.keys(nextFieldErrors).length === 0 && !nextSignatureError,
      fieldErrors: nextFieldErrors,
      signatureError: nextSignatureError,
    };
  };

  const validateFieldsByName = (fieldNames: string[]) => {
    const nextFieldErrors: Record<string, string> = {};
    fieldNames.forEach((fieldName) => {
      if (!asString(formData[fieldName]).trim()) {
        nextFieldErrors[fieldName] = t('RequiredFieldError', 'This field is required');
      }
    });
    setFieldErrors(nextFieldErrors);
    return Object.keys(nextFieldErrors).length === 0;
  };

  const deriveTbAssessmentPatch = (data: CaregiverData) => {
    const today = new Date().toISOString().slice(0, 10);
    const needsReview = TB_NEEDS_REVIEW_FIELDS.some((fieldName) => yesNoValue(data[fieldName]) === 'Yes');
    return {
      tbAssessmentStatus: needsReview ? 'NEEDS_FOLLOW_UP' : 'REVIEWED',
      tbAssessmentReviewDate: needsReview ? null : today,
    };
  };

  const saveCurrentStep = async (advance: boolean, targetStep?: number) => {
    const validation = validateCurrentFields();
    if (!validation.isValid) {
      const validationMessage = t('caregiver.submitValidationFailed', 'Please fix the highlighted fields before submitting.');
      setError(validationMessage);
      void reportClientEvent({
        level: 'warn',
        context: 'Caregiver step validation blocked',
        details: submitDebugDetails({
          advance,
          targetStep,
          fieldErrors: validation.fieldErrors,
          signatureError: validation.signatureError,
          visibleFieldNames: currentFields.map((field) => field.name),
        }),
      });
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const nextStep = advance ? Math.min(targetStep ?? currentStep + 1, sections.length - 1) : currentStep;
      const nextStatus = sections[nextStep].status;
      const today = new Date().toISOString().slice(0, 10);
      const hasAllergyFields = currentFields.some((field) => ALLERGY_FIELD_VALUE[field.name]);
      const allergyPatch = hasAllergyFields ? { allergiesAfraidOf: composeAllergySelections(allergySelections) } : {};
      const currentStepPatch = currentFields.reduce<CaregiverData>((patch, field) => {
        if (field.type === 'signature' || field.type === 'summary' || field.type === 'heading' || ALLERGY_FIELD_VALUE[field.name]) {
          return patch;
        }
        patch[field.name] = fieldPayloadValue(field, formData);
        return patch;
      }, {});
      const tbPatch = isOnboardingTbStep ? deriveTbAssessmentPatch(formData) : {};
      const submittedDates = advance && isFinalEditableStep
        ? {
          ...(kind === 'application' ? { applicationDate: today } : {}),
          ...(kind === 'onboarding' ? { onboardingSignatureDate: today, hepBDeclined: today } : {}),
          ...(kind === 'onboarding' && showFluDeclination && asString(formData.fluDeclinationReason).trim()
            ? { fluVaccineDeclined: today }
            : {}),
        }
        : {};

      const isSubmitStepAdvance = advance && (isApplicationSubmitStep || isOnboardingSubmitStep);
      if (isSubmitStepAdvance) {
        const result = await submitCaregiver(
          kind,
          {
            ...currentStepPatch,
            ...allergyPatch,
            ...tbPatch,
            ...submittedDates,
          },
          asString(formData[signatureFieldName]) || undefined,
        );
        setSubmissionProcessing(result.submission);
        setGeneratedDocs([]);
        setFormData(result.caregiver);
        setCurrentStep(nextStep);
        return;
      }

      const saved = await saveCaregiver({
        ...currentStepPatch,
        ...allergyPatch,
        ...tbPatch,
        ...submittedDates,
        portalStatus: nextStatus,
      });
      setFormData(saved);
      if (advance) setCurrentStep(nextStep);
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : t('caregiver.saveFailed', 'Failed to save');
      setError(message);
      void reportClientEvent({
        level: 'error',
        context: 'Caregiver saveCurrentStep failed',
        details: submitDebugDetails({
          advance,
          targetStep,
          message,
        }),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStepClick = (step: number) => {
    if (!isWorkflowLocked && step < currentStep) {
      setCurrentStep(step);
    }
  };

  const handleFileUpload = async (field: CaregiverFieldConfig, file: File | null) => {
    if (!file) return;
    setSaving(true);
    setError(null);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      await uploadDocument(field.name, file.name, dataUrl);
      setFormData((previous) => ({ ...previous, [field.name]: true }));
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : t('UploadFailed', 'Upload failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSignatureEnd = () => {
    const signaturePad = signaturePadRef.current;
    if (!signaturePad || signaturePad.isEmpty()) return;

    const dataUrl = signaturePad.toDataURL('image/png');
    if (!isSignatureValid(signaturePad, dataUrl)) {
      setSignatureError(t('signature.error', 'Signature is required'));
      setFormData((previous) => ({
        ...previous,
        [signatureFieldName]: '',
        [signatureSavedFieldName]: false,
      }));
      return;
    }

    setSignatureError(null);
    setError(null);
    setFormData((previous) => ({
      ...previous,
      [signatureFieldName]: dataUrl,
      [signatureSavedFieldName]: true,
    }));
  };

  const openTemplatePreview = async (
    title: string,
    templateName: string,
    language = 'English'
  ) => {
    setTemplateDialog({ title, templateName, language, blob: null, url: null, loading: true, error: null });
    try {
      const blob = await getTemplatePdf(templateName, { formName: 'caregiver', language });
      const url = URL.createObjectURL(blob);
      setTemplateDialog({ title, templateName, language, blob, url, loading: false, error: null });
    } catch (previewError) {
      setTemplateDialog({
        title,
        templateName,
        language,
        blob: null,
        url: null,
        loading: false,
        error: previewError instanceof Error ? previewError.message : t('documents.loadPdfFailed', 'Failed to load PDF'),
      });
    }
  };

  const openFilledTemplatePreview = async (
    title: string,
    templateName: string,
    language = 'English'
  ) => {
    setTemplateDialog({ title, templateName, language, blob: null, url: null, loading: true, error: null });
    try {
      const result = await generateDocuments({
        documentSet: 'onboarding',
        singleDoc: templateName,
        saveToDB: false,
      });
      const inlineDoc = result.inlineDocs?.[0];
      if (!inlineDoc) {
        throw new Error(result.missingTemplates.length
          ? `Missing templates: ${result.missingTemplates.join(', ')}`
          : t('documents.noDocumentReturned', 'No document returned'));
      }
      const bytes = Uint8Array.from(atob(inlineDoc.pdfBase64), (character) => character.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setTemplateDialog({ title, templateName, language, blob, url, loading: false, error: null });
    } catch (previewError) {
      setTemplateDialog({
        title,
        templateName,
        language,
        blob: null,
        url: null,
        loading: false,
        error: previewError instanceof Error ? previewError.message : t('documents.loadPdfFailed', 'Failed to load PDF'),
      });
    }
  };

  const handleTemplateDownload = async () => {
    if (!templateDialog) return;
    setSaving(true);
    setError(null);
    try {
      const blob = templateDialog.blob ?? await getTemplatePdf(templateDialog.templateName, {
        formName: 'caregiver',
        language: templateDialog.language,
      });
      downloadBlob(blob, `${templateDialog.templateName}.pdf`);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : t('documents.downloadPdfFailed', 'Failed to download PDF'));
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratedDocumentDownload = async (document: GeneratedDocument) => {
    setSaving(true);
    setError(null);
    try {
      const blob = await getGeneratedDocumentPdf(document.id);
      downloadBlob(blob, `${document.documentName}.pdf`);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : t('documents.downloadFailed', 'Failed to download document'));
    } finally {
      setSaving(false);
    }
  };

  const openGeneratedDocumentPreview = async (document: GeneratedDocument) => {
    if (generatedDocDialog?.url) URL.revokeObjectURL(generatedDocDialog.url);
    setGeneratedDocDialog({ document, url: null, loading: true, error: null });
    try {
      const blob = await getGeneratedDocumentPdf(document.id);
      const url = URL.createObjectURL(blob);
      setGeneratedDocDialog({ document, url, loading: false, error: null });
    } catch (previewError) {
      setGeneratedDocDialog({
        document,
        url: null,
        loading: false,
        error: previewError instanceof Error ? previewError.message : t('documents.loadPdfPreviewFailed', 'Failed to load PDF preview'),
      });
    }
  };

  const closeGeneratedDocumentPreview = () => {
    if (generatedDocDialog?.url) URL.revokeObjectURL(generatedDocDialog.url);
    setGeneratedDocDialog(null);
  };

  const handleSummaryDownload = async () => {
    setSaving(true);
    setError(null);
    try {
      const blob = await getSummaryPdf(kind);
      downloadBlob(blob, `${kind}-summary.pdf`);
    } catch (summaryError) {
      setError(summaryError instanceof Error ? summaryError.message : t('documents.summaryDownloadFailed', 'Failed to download summary'));
    } finally {
      setSaving(false);
    }
  };

  const clearSignature = () => {
    signaturePadRef.current?.clear();
    setSignatureError(null);
    setFormData((previous) => ({
      ...previous,
      [signatureFieldName]: '',
      [signatureSavedFieldName]: false,
    }));
    setError(null);
  };

  const renderReviewFormTiles = () => {
    const affidavitLanguage = documentLanguageFromPrimaryLanguage(formData.primaryLanguage);
    const tiles = [
      {
        title: 'CHRC',
        body: t('reviewForms.chrcText', 'Click here to view a sample CHRC form'),
        onClick: () => void openTemplatePreview('CHRC', 'CHRC', 'English'),
      },
      {
        title: t('reviewForms.affidavit', 'Affidavit'),
        body: t('reviewForms.affidavitText', 'Click here to view a sample Affidavit form'),
        onClick: () => void openTemplatePreview(t('reviewForms.affidavit', 'Affidavit'), 'Affidavit', affidavitLanguage),
      },
    ];

    return (
      <Grid container spacing={2} justifyContent="center" alignItems="stretch" sx={{ mt: 2 }}>
        {tiles.map((tile) => (
          <Grid key={tile.title} sx={{ display: 'flex' }} size={{ xs: 12, sm: 4 }}>
            <Box
              onClick={tile.onClick}
              sx={{
                flex: 1,
                cursor: 'pointer',
                border: '1px solid #ddd',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                '&:hover': { boxShadow: '0px 4px 10px #330662' },
              }}
            >
              <Typography variant="h5" sx={{ mb: 1 }}>
                {tile.title}
              </Typography>
              <Typography variant="body2">{tile.body}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderSubmittedField = (field: CaregiverFieldConfig) => {
    if (field.type === 'heading' || field.type === 'summary') return null;
    if (field.type === 'signature') {
      return (
        <Grid key={field.name} size={{ xs: 12, sm: 6 }}>
          <Typography variant="caption" color="text.secondary">
            {fieldLabel(field)}
          </Typography>
          <Typography variant="body2">{t('Completed', 'Completed')}</Typography>
        </Grid>
      );
    }
    if (ALLERGY_FIELD_VALUE[field.name]) return null;

    const value = translatedFieldValue(field);
    if (!value) return null;

    return (
      <Grid key={field.name} size={{ xs: 12, sm: 6 }}>
        <Typography variant="caption" color="text.secondary">
          {fieldLabel(field)}
        </Typography>
        <Typography variant="body2">{value}</Typography>
      </Grid>
    );
  };

  const renderSubmittedCard = (card: (typeof currentSection.cards)[number]) => {
    const fields = card.fields.map(renderSubmittedField).filter(Boolean);
    if (fields.length === 0 && !card.description) return null;

    return (
      <Box key={card.title} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {card.icon && <Box sx={{ color: FORM_ICON_COLOR, display: 'flex' }}>{iconFor(card.icon)}</Box>}
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {translateText(card.title)}
          </Typography>
        </Box>
        {card.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: fields.length ? 1.5 : 0 }}>
            {translateText(card.description)}
          </Typography>
        )}
        {fields.length > 0 && (
          <Grid container spacing={1.5}>
            {fields}
          </Grid>
        )}
      </Box>
    );
  };

  const renderGeneratedDocuments = () => (
    <>
      {generatedDocs.length === 0 && submissionProcessing?.status !== 'pending' && submissionProcessing?.status !== 'processing' && (
        <Typography variant="body2" color="text.secondary">
          {t('documents.noneGenerated', 'No documents generated yet.')}
        </Typography>
      )}

      {generatedDocs.length > 0 && (
        <List dense>
          {generatedDocs.map((document) => (
            <ListItem key={document.id} divider>
              <ListItemText
                primary={document.documentName.replace(/\.pdf$/i, '')}
                secondary={document.createdAt ? new Date(document.createdAt).toLocaleDateString() : undefined}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => void openGeneratedDocumentPreview(document)}
                  title={t('documents.viewPdf', 'View PDF')}
                  sx={{ color: FORM_ICON_COLOR }}
                >
                  <VisibilityIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Dialog open={Boolean(generatedDocDialog)} onClose={closeGeneratedDocumentPreview} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            bgcolor: '#330662',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="h6">{generatedDocDialog?.document.documentName}</Typography>
          {generatedDocDialog?.url && (
            <Button
              variant="contained"
              color="inherit"
              onClick={() => void handleGeneratedDocumentDownload(generatedDocDialog.document)}
              sx={{ color: '#330662' }}
            >
              {t('documents.downloadPdf', 'Download PDF')}
            </Button>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f5f5f5', minHeight: 500 }}>
          {generatedDocDialog?.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          )}
          {generatedDocDialog?.error && <Alert severity="error">{generatedDocDialog.error}</Alert>}
          {generatedDocDialog?.url && !generatedDocDialog.loading && !generatedDocDialog.error && (
            <Box
              component="iframe"
              src={generatedDocDialog.url}
              title={generatedDocDialog.document.documentName}
              sx={{ width: '100%', height: 650, border: 0, bgcolor: 'white' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeGeneratedDocumentPreview}>{t('Close', 'Close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );

  const renderSubmittedStep = () => {
    const submittedStatus = kind === 'application' ? 'App Submitted' : 'Review';
    const reviewSections = sections.filter((section) => section.status !== submittedStatus);
    const summaryLabel = kind === 'application' ? 'application' : 'onboarding';
    return (
      <>
        <Box textAlign="center" sx={{ mb: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50', mb: 1 }} />
          <Typography variant="h4" gutterBottom>
            {t('submission.thankYou', 'Thank You for Submitting!')}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t('submission.summary', 'Below is a summary of your {{form}}:', { form: t(summaryLabel, summaryLabel) })}
          </Typography>
        </Box>

        {(submissionProcessing?.status === 'pending' || submissionProcessing?.status === 'processing') && (
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            {t(
              'submission.documentsProcessing',
              'Your submission is complete. Your documents are being prepared and will appear here automatically. You can safely close this page.',
            )}
            <LinearProgress sx={{ mt: 1.5 }} />
          </Alert>
        )}
        {submissionProcessing?.status === 'failed' && (
          <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
            {t(
              'submission.documentsDelayed',
              'Your submission is complete, but document preparation is delayed. Our team has been notified.',
            )}
          </Alert>
        )}

        {reviewSections.map((section, index) => (
          <Accordion key={section.status} defaultExpanded={index === 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>
                {section.title === 'App Consent' ? t('CHRC & Signature', 'CHRC & Signature') : translateText(section.title)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {section.cards.map(renderSubmittedCard)}
              {section.status === 'Availability' && asString(formData.allergiesAfraidOf) && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('Allergies/Fears', 'Allergies/Fears')}
                  </Typography>
                  <Typography variant="body2">{translateDelimitedValue(asString(formData.allergiesAfraidOf).split(';').join(', '))}</Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography fontWeight={600}>{t('Documents', 'Documents')}</Typography>
              {generatedDocs.length > 0 && <Chip label={generatedDocs.length} size="small" color="primary" />}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('documents.profileLanguageNotice', 'Documents are generated in your profile language: {{language}}.', { language: documentLanguageLabel })}
          </Alert>
            {renderGeneratedDocuments()}
          </AccordionDetails>
        </Accordion>
      </>
    );
  };

  const renderField = (field: CaregiverFieldConfig) => {
    const label = fieldLabel(field);
    const translatedHelperText = helperText(field);

    if (field.type === 'heading') {
      return (
        <Typography key={field.name} variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1 }}>
          {label}
        </Typography>
      );
    }

    if (field.type === 'radio') {
      const isAllergyField = Boolean(ALLERGY_FIELD_VALUE[field.name]);
      const value = isAllergyField ? allergySelections[field.name] ?? '' : asString(formData[field.name]);
      return (
        <FormControl
          key={field.name}
          component="fieldset"
          fullWidth
          required={field.required}
          error={Boolean(fieldErrors[field.name])}
        >
          <FormLabel
            component="legend"
            sx={{
              color: 'black',
              fontWeight: 'bold',
              mb: -1.5,
            }}
          >
            {label}
          </FormLabel>
          <RadioGroup
            row
            name={field.name}
            value={value}
            onChange={(event) => {
              if (isAllergyField) {
                setAllergySelections((previous) => ({ ...previous, [field.name]: event.target.value }));
                setFieldErrors((previous) => {
                  if (!previous[field.name]) return previous;
                  const next = { ...previous };
                  delete next[field.name];
                  return next;
                });
                setError(null);
              } else {
                updateValue(field.name, event.target.value);
              }
            }}
          >
            {(field.options ?? []).map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio sx={{ color: '#702b9d', '&.Mui-checked': { color: '#702b9d' } }} />}
                label={translateText(option)}
              />
            ))}
          </RadioGroup>
          {fieldErrors[field.name] && <FormHelperText error>{fieldErrors[field.name]}</FormHelperText>}
        </FormControl>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <FormControlLabel
          key={field.name}
          control={
            <Checkbox
              checked={asBoolean(formData[field.name])}
              onChange={(event) => updateValue(field.name, event.target.checked)}
            />
          }
          label={label}
        />
      );
    }

    if (field.type === 'multiselect') {
      const selected = normalizeMultiValues(formData[field.name], field.options ?? []);
      return (
        <MultiSelectPicklist
          key={field.name}
          name={field.name}
          label={label}
          value={selected.join(';')}
          options={(field.options ?? []).map((option) => ({ value: option, label: translateText(option) }))}
          onChange={(value) => updateValue(field.name, value)}
          required={field.required}
          disabled={field.disabled}
          error={Boolean(fieldErrors[field.name])}
          helperText={fieldErrors[field.name] || translatedHelperText}
        />
      );
    }

    if (field.type === 'file') {
      return (
        <Stack key={field.name} spacing={1}>
          <Typography variant="subtitle2">{label}</Typography>
          <Typography variant="caption" color="text.secondary">
            {translateText(fileLabel(formData, field.name))}
          </Typography>
          {fieldErrors[field.name] && (
            <Typography variant="caption" color="error">
              {fieldErrors[field.name]}
            </Typography>
          )}
          <Button component="label" variant="outlined" disabled={saving}>
            {t('Upload', 'Upload')}
            <input
              hidden
              type="file"
              accept=".pdf,.doc,.docx,image/*"
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                void handleFileUpload(field, event.target.files?.[0] ?? null);
                event.target.value = '';
              }}
            />
          </Button>
        </Stack>
      );
    }

    if (field.type === 'signature') {
      return (
        <Stack key={field.name} spacing={1.5}>
          <Typography variant="subtitle2">{label}</Typography>
          <Alert severity={signatureSaved ? 'success' : 'info'}>
            {signatureSaved
              ? t('signature.saved', 'Signature saved.')
              : t(
                'signature.instructions',
                'By signing below, I certify that all information provided is true and accurate to the best of my knowledge.'
              )}
          </Alert>
          <Box className="textBarrierDiv" />
          <Box
            sx={{
              border: signatureError ? '2px solid #d32f2f' : '1px solid #000000',
              width: '100%',
              height: 200,
              mb: 1,
            }}
          >
            <SignatureCanvas
              ref={signaturePadRef}
              onEnd={() => void handleSignatureEnd()}
              canvasProps={{
                height: 200,
                className: 'signatureCanvas',
              }}
            />
          </Box>
          {signatureError && (
            <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>
              {signatureError}
            </Typography>
          )}
        </Stack>
      );
    }

    if (field.type === 'summary') {
      return (
        <Stack key={field.name} spacing={1.5}>
          <Typography variant="subtitle2">{label}</Typography>
          {kind === 'application' && generatedDocs.length > 0 && (
            <Stack spacing={1}>
              {generatedDocs.map((document) => (
                <Box
                  key={document.id}
                  sx={{
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Typography variant="body2">{document.documentName}</Typography>
                  <Button size="small" variant="outlined" onClick={() => void handleGeneratedDocumentDownload(document)}>
                    {t('documents.downloadPdf', 'Download PDF')}
                  </Button>
                </Box>
              ))}
            </Stack>
          )}
          <Button variant="outlined" onClick={handleSummaryDownload} disabled={saving}>
            {t('documents.downloadPdf', 'Download PDF')}
          </Button>
        </Stack>
      );
    }

    const inputMask = inputMaskFor(field);
    if (inputMask) {
      const value = asString(formData[field.name]);
      return (
        <FormTextField
          key={field.name}
          inputMask={inputMask}
          value={value}
          onChange={(event: ChangeEvent<HTMLInputElement>) => updateValue(field.name, event.target.value)}
          maskChar={null}
          label={label}
          required={field.required}
          disabled={field.disabled}
          fullWidth
          error={Boolean(fieldErrors[field.name])}
          helperText={fieldErrors[field.name] || translatedHelperText}
        />
      );
    }

    if (field.type === 'select') {
      const selectOptions = selectOptionsForField(field);
      const normalizedSelectValue = normalizeSelectOptionValue(formData[field.name], selectOptions);
      return (
        <Picklist
          key={field.name}
          name={field.name}
          label={label}
          value={normalizedSelectValue}
          options={selectOptions.map((option) => ({ ...option, label: translateText(option.label) }))}
          onChange={(value) => updateValue(field.name, value)}
          required={field.required}
          disabled={field.disabled}
          error={Boolean(fieldErrors[field.name])}
          helperText={fieldErrors[field.name] || translatedHelperText}
        />
      );
    }

    if (field.type === 'date') {
      return (
        <DateField
          key={field.name}
          label={label}
          value={asString(formData[field.name])}
          onChange={(event) => updateValue(field.name, event.target.value)}
          required={field.required}
          disabled={field.disabled}
          error={Boolean(fieldErrors[field.name])}
          fullWidth
          helperText={fieldErrors[field.name] || translatedHelperText}
        />
      );
    }

    return (
      <FormTextField
        key={field.name}
        label={label}
        value={asString(formData[field.name])}
        onChange={(event) => updateValue(field.name, event.target.value)}
        type={field.type === 'email' ? 'email' : field.type === 'tel' ? 'tel' : 'text'}
        required={field.required}
        disabled={field.disabled}
        error={Boolean(fieldErrors[field.name])}
        multiline={field.type === 'textarea'}
        minRows={field.type === 'textarea' ? 3 : undefined}
        fullWidth
        helperText={fieldErrors[field.name] || translatedHelperText}
      />
    );
  };

  const renderTaxSelect = (
    fieldName: string,
    label: string,
    options: Array<string | { value: string; label: string }>,
    value: string,
    required = false
  ) => (
    <Picklist
      name={fieldName}
      label={label}
      value={value}
      options={options.map<PicklistOption>((option) => (
        typeof option === 'string'
          ? { value: option, label: translateText(option) }
          : { ...option, label: translateText(option.label) }
      ))}
      onChange={(nextValue) => updateValue(fieldName, nextValue)}
      required={required}
      error={Boolean(fieldErrors[fieldName])}
      helperText={fieldErrors[fieldName] || ''}
    />
  );

  const renderMaskedTaxField = (
    fieldName: string,
    label: string,
    mask: string,
    helperText = '',
    required = false
  ) => (
    <FormTextField
      inputMask={mask}
      value={asString(formData[fieldName])}
      onChange={(event: ChangeEvent<HTMLInputElement>) => updateValue(fieldName, event.target.value)}
      maskChar={null}
      label={label}
      required={required}
      fullWidth
      error={Boolean(fieldErrors[fieldName])}
      helperText={fieldErrors[fieldName] || helperText}
    />
  );

  const renderTaxFormsStep = () => {
    const dependentsUnder17 = asNonNegativeInteger(formData.dependentsUnder17);
    const otherDependents = asNonNegativeInteger(formData.otherDependents);
    const dependentCredit = (dependentsUnder17 * 2200) + (otherDependents * 500);

    return (
      <Box mt="25px" sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormSection
              title={t('tax.w4Title', "W-4 Employee's Withholding Certificate")}
              icon={iconFor('accountBalance')}
              action={(
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => void openTemplatePreview('W-4', 'W-4 Form', 'English')}
                  sx={{ color: '#702b9d', borderColor: '#702b9d' }}
                >
                  {t('tax.viewDocument', 'View Document')}
                </Button>
              )}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('tax.w4Step1c', 'Step 1(c): Filing Status')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {renderTaxSelect(
                    'federalFilingStatus',
                    t('tax.w4FilingStatus', 'Filing Status'),
                    FEDERAL_FILING_STATUS_OPTIONS,
                    asString(formData.federalFilingStatus),
                    true
                  )}
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>
                {t('tax.w4Step2', 'Step 2: Multiple Jobs or Spouse Works')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        sx={{ color: '#702b9d', '&.Mui-checked': { color: '#702b9d' } }}
                        checked={asBoolean(formData.multipleJobs)}
                        onChange={(event) => updateValue('multipleJobs', event.target.checked)}
                        name="multipleJobs"
                      />
                    )}
                    label={t('tax.w4MultipleJobs', "Check if you have more than one job at a time, or if you're married filing jointly and your spouse also works")}
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 3, mb: 0 }}>
                {t('tax.w4Step3', 'Step 3: Claim Dependent and Other Credits')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('tax.w4Step3Instructions', 'If your total income will be $200,000 or less ($400,000 or less if married filing jointly):')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormTextField
                    id="dependentsUnder17"
                    name="dependentsUnder17"
                    label={t('tax.w4DependentsUnder17', 'Multiply the number of qualifying children under age 17 by $2,200')}
                    type="number"
                    value={dependentsUnder17}
                    onChange={(event) => updateDependentCounts('dependentsUnder17', event.target.value)}
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      inputProps: { min: 0 },
                      endAdornment: (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          × $2,200 = ${dependentsUnder17 * 2200}
                        </Typography>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormTextField
                    id="otherDependents"
                    name="otherDependents"
                    label={t('tax.w4OtherDependents', 'Multiply the number of other dependents by $500')}
                    type="number"
                    value={otherDependents}
                    onChange={(event) => updateDependentCounts('otherDependents', event.target.value)}
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      inputProps: { min: 0 },
                      endAdornment: (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          × $500 = ${otherDependents * 500}
                        </Typography>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormTextField
                    id="dependentCredit"
                    name="dependentCredit"
                    label={t('tax.w4TotalDependentCredit', 'Total (Step 3)')}
                    value={dependentCredit > 0 ? `$${dependentCredit}` : '$0'}
                    variant="outlined"
                    fullWidth
                    readOnly
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        cursor: 'default',
                        backgroundColor: '#f5f5f5',
                      },
                      '& .MuiInputBase-input': {
                        cursor: 'default',
                        pointerEvents: 'none',
                      },
                    }}
                    helperText={t('tax.w4TotalDependentCreditHelper', 'Enter values above for qualifying children and other dependents. Your dependent credit will automatically calculate based on your entry.')}
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 3, mb: 0 }}>
                {t('tax.w4Step4', 'Step 4 (optional): Other Adjustments')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('tax.w4Step4Instructions', 'Complete this section only if it applies to you.')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {renderMaskedTaxField('otherIncome', t('tax.w4OtherIncome', 'Other income (not from jobs)'), TAX_AMOUNT_MASK, t('tax.w4OtherIncomeHelper', "If you want tax withheld for other income you expect this year that won't have withholding (interest, dividends, retirement income), enter the amount here."))}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {renderMaskedTaxField('fedDeductions', t('tax.w4Deductions', 'Deductions'), TAX_AMOUNT_MASK, t('tax.w4DeductionsHelper', 'If you expect to claim deductions other than the standard deduction and want to reduce your withholding, enter the amount here.'))}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {renderMaskedTaxField('fedAdditionalWithholdingAmount', t('tax.w4ExtraWithholding', 'Extra withholding'), TAX_AMOUNT_MASK, t('tax.w4ExtraWithholdingHelper', 'Enter any additional tax you want withheld each pay period.'))}
                </Grid>
              </Grid>
            </FormSection>
          </Grid>

          <Grid size={12}>
            <FormSection
              title={t('tax.it2104Title', "IT-2104 Employee's Withholding Allowance Certificate")}
              icon={iconFor('accountBalance')}
              action={(
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => void openTemplatePreview('IT-2104', 'NYS IT-2104', 'English')}
                  sx={{ color: '#702b9d', borderColor: '#702b9d' }}
                >
                  {t('tax.viewDocument', 'View Document')}
                </Button>
              )}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('tax.it2104FilingStatusSection', 'Filing Status')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {renderTaxSelect(
                    'nyFilingStatus',
                    t('tax.it2104FilingStatus', 'Filing Status'),
                    NY_FILING_STATUS_OPTIONS,
                    asString(formData.nyFilingStatus),
                    true
                  )}
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>
                {t('tax.it2104ResidencySection', 'Residency')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {renderTaxSelect(
                    'residentOfNewYorkCity',
                    t('tax.it2104ResidentNYC', 'Are you a resident of New York City (this includes the Bronx, Brooklyn, Manhattan, Queens, and Staten Island)?'),
                    YES_NO_OPTIONS,
                    yesNoValue(formData.residentOfNewYorkCity),
                    true
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {renderTaxSelect(
                    'residentOfYonkers',
                    t('tax.it2104ResidentYonkers', 'Are you a resident of Yonkers?'),
                    YES_NO_OPTIONS,
                    yesNoValue(formData.residentOfYonkers),
                    true
                  )}
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>
                {t('tax.it2104AllowancesSection', 'Allowances')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {renderMaskedTaxField('nysAllowances', t('tax.it2104Line1', 'Total number of allowances you are claiming for New York State and Yonkers'), TAX_ALLOWANCE_MASK, '', true)}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {renderMaskedTaxField('nycAllowances', t('tax.it2104Line2', 'Total number of allowances for New York City'), TAX_ALLOWANCE_MASK, '', true)}
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 3, mb: 0 }}>
                {t('tax.it2104AdditionalWithholdingSection', 'Additional Withholding Per Pay Period')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('tax.it2104AdditionalWithholdingInstructions', 'Use these fields to have additional withholding per pay period under special agreement with your employer.')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {renderMaskedTaxField('nysAdditionalWithholdingAmount', t('tax.it2104Line3', 'New York State amount'), TAX_AMOUNT_MASK)}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {renderMaskedTaxField('nycAdditionalWithholdingAmount', t('tax.it2104Line4', 'New York City amount'), TAX_AMOUNT_MASK)}
                </Grid>
              </Grid>
            </FormSection>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderFieldGrid = (fields: CaregiverFieldConfig[]) => (
    <Grid container spacing={2}>
      {fields.filter((field) => isFieldVisible(field, formData)).map((field) => (
        <Grid
          key={field.name}
          size={
            field.width === 'full'
              ? 12
              : {
                xs: 12,
                sm: field.width === 'third' ? 4 : field.width === 'quarter' ? 3 : 6,
              }
          }
        >
          {renderField(field)}
        </Grid>
      ))}
    </Grid>
  );

  const renderPreferencesStep = () => {
    const paymentCard = currentSection.cards.find((card) => card.title === 'Payment Method');
    const patientPreferencesCard = currentSection.cards.find((card) => card.title === 'Patient & Environment Preferences');
    const availabilityCard = currentSection.cards.find((card) => card.title === 'Availability');
    const paymentField = paymentCard?.fields.find((field) => field.name === 'paymentMethod');
    const directDepositFields = paymentCard?.fields.filter((field) => field.name !== 'paymentMethod') ?? [];
    const showDirectDepositFields = normalizeOptionValue(formData.paymentMethod, ['Direct Deposit']) === 'Direct Deposit';

    return (
      <Box mt="25px">
        {paymentCard && (
          <FormSection title={t('Payment Method', 'Payment Method')} icon={iconFor(paymentCard.icon)} sx={{ mt: 3 }}>
            {paymentField && (
              <Grid container spacing={2}>
                <Grid size={12}>
                  {renderField(paymentField)}
                </Grid>
              </Grid>
            )}
            {showDirectDepositFields && (
              <>
                <Box
                  component="img"
                  src="/images/BlankCheck.png"
                  alt={t('Blank Check Example', 'Blank Check Example')}
                  sx={{
                    display: 'block',
                    maxWidth: '100%',
                    height: 'auto',
                    mx: 'auto',
                    mt: 3,
                    mb: 2,
                  }}
                />
                {renderFieldGrid(directDepositFields)}
              </>
            )}
          </FormSection>
        )}

        {patientPreferencesCard && (
          <FormSection
            title={t('PatientEnvironmentPreferences', 'Patient & Environment Preferences')}
            icon={iconFor(patientPreferencesCard.icon)}
            sx={{ mb: 2, mt: 3 }}
          >
            {renderFieldGrid(patientPreferencesCard.fields)}
          </FormSection>
        )}

        {availabilityCard && (
          <FormSection
            title={t('Availability', 'Availability')}
            icon={iconFor(availabilityCard.icon)}
            sx={{ mb: 2, mt: 3 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
              <strong>{t('Morning', 'Morning')}</strong>
              {' '}
              ({t('before 12pm', 'before 12pm')})
              {' · '}
              <strong>{t('Afternoon', 'Afternoon')}</strong>
              {' '}
              ({t('12pm-6pm', '12pm-6pm')})
              {' · '}
              <strong>{t('Night', 'Night')}</strong>
              {' '}
              ({t('after 6pm', 'after 6pm')})
              {' · '}
              <strong>{t('Live-In', 'Live-In')}</strong>
              {' '}
              ({t('24hr Shift', '24hr Shift')})
            </Typography>
            {renderFieldGrid(availabilityCard.fields)}
          </FormSection>
        )}
      </Box>
    );
  };

  const renderTbQuestions = (questions: WotcQuestion[]) => (
    <Card variant="outlined" sx={{ mb: 1 }}>
      <CardContent>
        {questions === TB_SYMPTOM_QUESTIONS && (
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#702b9d' }}>
            {t('TB_Symptoms_Header__c', 'At this time, do you have any of these symptoms?')}
          </Typography>
        )}
        {questions.map((question, index) => (
          <Box key={question.fieldName} sx={{ mb: index === questions.length - 1 ? 0 : 3, width: '100%' }}>
            <FormControl component="fieldset" fullWidth error={Boolean(fieldErrors[question.fieldName])}>
              <FormLabel component="legend" sx={{ color: 'black', mb: 1 }}>
                <Typography component="span" sx={{ fontWeight: 'bold' }}>
                  {index + 1}
                  . {t(question.key, question.fallback)}
                </Typography>
              </FormLabel>
              <RadioGroup
                row
                name={question.fieldName}
                value={yesNoValue(formData[question.fieldName])}
                onChange={(event) => updateValue(question.fieldName, event.target.value)}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio sx={{ color: '#702b9d', '&.Mui-checked': { color: '#702b9d' } }} />}
                  label={t('Yes', 'Yes')}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio sx={{ color: '#702b9d', '&.Mui-checked': { color: '#702b9d' } }} />}
                  label={t('No', 'No')}
                />
              </RadioGroup>
              {fieldErrors[question.fieldName] && <FormHelperText error>{fieldErrors[question.fieldName]}</FormHelperText>}
            </FormControl>
          </Box>
        ))}
      </CardContent>
    </Card>
  );

  const renderTbAssessmentStep = () => {
    const progressValue = (currentTbStep / 2) * 100;

    return (
      <Box mt="25px" sx={{ color: 'black' }}>
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 2, mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box
              onClick={() => void openTemplatePreview(t('TB Assessment', 'TB Assessment'), 'TB Assessment', documentLanguage)}
              sx={{
                cursor: 'pointer',
                border: '1px solid #ddd',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                '&:hover': {
                  boxShadow: '0px 4px 10px #330662',
                },
              }}
            >
              <Typography variant="h5" sx={{ mb: 1 }}>
                {t('TB Assessment', 'TB Assessment')}
              </Typography>
              <Typography variant="body2">
                {t('TBAssessmentFileText', 'Click to view the TB Assessment document')}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              {t('tbAssessment.stepProgress', {
                current: currentTbStep,
                total: 2,
                defaultValue: 'Step {{current}} of {{total}}',
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progressValue)}
              %
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#e0e0e0',
              '& .MuiLinearProgress-bar': { bgcolor: '#702b9d' },
            }}
          />
        </Box>

        {currentTbStep === 1 ? renderTbQuestions(TB_RISK_QUESTIONS) : renderTbQuestions(TB_SYMPTOM_QUESTIONS)}
      </Box>
    );
  };

  const renderWotcBullets = (bullets: WotcBullet[]) => (
    <List component="ul" sx={{ listStyleType: 'disc', pl: 3, py: 0 }}>
      {bullets.map((bullet) => (
        <ListItem
          key={bullet.key}
          component="li"
          sx={{
            display: 'list-item',
            py: 0.25,
            pl: 0,
            flexDirection: bullet.children ? 'column' : undefined,
            alignItems: bullet.children ? 'flex-start' : undefined,
          }}
        >
          <Typography component="span" variant="body2">
            {t(bullet.key, bullet.fallback)}
          </Typography>
          {bullet.children && (
            <List component="ul" sx={{ listStyleType: 'none', pl: 2, py: 0.25 }}>
              {bullet.children.map((child) => (
                <ListItem key={child.key} component="li" sx={{ display: 'list-item', py: 0.25, pl: 0 }}>
                  <Typography component="span" variant="body2">
                    {t(child.key, child.fallback)}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </ListItem>
      ))}
    </List>
  );

  const renderWotcStep = () => {
    const question = WOTC_QUESTIONS[currentWotcStep - 1];
    const progressValue = (currentWotcStep / WOTC_QUESTIONS.length) * 100;
    const language = documentLanguageFromPrimaryLanguage(formData.primaryLanguage);

    return (
      <Box mt="25px" sx={{ color: 'black' }}>
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box
              onClick={() => void openTemplatePreview(t('WOTCFileTitle', 'Work Opportunity Credit: Form 8850'), 'WOTC', language)}
              sx={{
                cursor: 'pointer',
                border: '1px solid #ddd',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                '&:hover': {
                  boxShadow: '0px 4px 10px #330662',
                },
              }}
            >
              <Typography variant="h5" sx={{ mb: 1 }}>
                {t('WOTCFileTitle', 'Work Opportunity Credit: Form 8850')}
              </Typography>
              <Typography variant="body2">
                {t('WOTCFileText', 'Click here to view a sample of the WOTC 8850 form')}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              {t('wotc.stepProgress', {
                current: currentWotcStep,
                total: WOTC_QUESTIONS.length,
                defaultValue: 'Step {{current}} of {{total}}',
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progressValue)}
              %
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#e0e0e0',
              '& .MuiLinearProgress-bar': { bgcolor: '#702b9d' },
            }}
          />
        </Box>

        <Card variant="outlined" sx={{ mb: 1 }}>
          <CardContent>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ color: 'black' }}>
                <Typography component="span" sx={{ fontWeight: 'bold' }}>
                  {t(question.key, question.fallback)}
                </Typography>
                {question.bullets && renderWotcBullets(question.bullets)}
              </FormLabel>
              <RadioGroup
                name={question.fieldName}
                value={yesNoValue(formData[question.fieldName])}
                onChange={(event) => updateValue(question.fieldName, event.target.value === 'Yes')}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio sx={{ color: '#702b9d', '&.Mui-checked': { color: '#702b9d' } }} />}
                  label={t('Yes', 'Yes')}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio sx={{ color: '#702b9d', '&.Mui-checked': { color: '#702b9d' } }} />}
                  label={t('No', 'No')}
                />
              </RadioGroup>
              {fieldErrors[question.fieldName] && <FormHelperText error>{fieldErrors[question.fieldName]}</FormHelperText>}
            </FormControl>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderYouthCreditStep = () => {
    const question = YOUTH_CREDIT_QUESTIONS[currentYouthCreditStep - 1];
    const progressValue = (currentYouthCreditStep / YOUTH_CREDIT_QUESTIONS.length) * 100;
    const language = documentLanguageFromPrimaryLanguage(formData.primaryLanguage);

    return (
      <Box mt="25px" sx={{ color: 'black' }}>
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box
              onClick={() => void openTemplatePreview(t('YouthCreditFileTitle', 'New York Youth Works Program - Youth Application'), 'Youth Credit', language)}
              sx={{
                cursor: 'pointer',
                border: '1px solid #ddd',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                '&:hover': {
                  boxShadow: '0px 4px 10px #330662',
                },
              }}
            >
              <Typography variant="h5" sx={{ mb: 1 }}>
                {t('YouthCreditFileTitle', 'New York Youth Works Program - Youth Application')}
              </Typography>
              <Typography variant="body2">
                {t('YouthCreditFileText', 'Click here to view a sample of the Youth Works Program Form')}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              {t('youthCredit.stepProgress', {
                current: currentYouthCreditStep,
                total: YOUTH_CREDIT_QUESTIONS.length,
                defaultValue: 'Step {{current}} of {{total}}',
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progressValue)}
              %
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#e0e0e0',
              '& .MuiLinearProgress-bar': { bgcolor: '#702b9d' },
            }}
          />
        </Box>

        <Card variant="outlined" sx={{ mb: 1 }}>
          <CardContent>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ color: 'black' }}>
                <Typography component="span" sx={{ fontWeight: 'bold' }}>
                  {t(question.key, question.fallback)}
                </Typography>
                {question.bullets && renderWotcBullets(question.bullets)}
              </FormLabel>
              <RadioGroup
                name={question.fieldName}
                value={yesNoValue(formData[question.fieldName])}
                onChange={(event) => updateValue(question.fieldName, event.target.value === 'Yes')}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio sx={{ color: '#702b9d', '&.Mui-checked': { color: '#702b9d' } }} />}
                  label={t('Yes', 'Yes')}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio sx={{ color: '#702b9d', '&.Mui-checked': { color: '#702b9d' } }} />}
                  label={t('No', 'No')}
                />
              </RadioGroup>
              {fieldErrors[question.fieldName] && <FormHelperText error>{fieldErrors[question.fieldName]}</FormHelperText>}
            </FormControl>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderDocumentReviewTiles = () => {
    const language = documentLanguageFromPrimaryLanguage(formData.primaryLanguage);
    const documents = [
      'W-4 Form',
      'NYS IT-2104',
      'NYS Wage Notice',
      'Direct Deposit',
      'Orientation Packet',
      'WOTC',
      'Hep B Decl.',
      'Declination Flu',
      'TB Assessment',
    ];

    return (
      <Grid container spacing={2} alignItems="stretch" sx={{ mt: 2 }}>
        {documents.map((documentName) => (
          <Grid key={documentName} sx={{ display: 'flex' }} size={{ xs: 12, sm: 4 }}>
            <Box
              onClick={() => void openFilledTemplatePreview(t(documentName, documentName), documentName, language)}
              sx={{
                cursor: 'pointer',
                border: '1px solid #ddd',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                '&:hover': {
                  boxShadow: '0px 4px 10px #330662',
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 1,
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              >
                {t(documentName, documentName)}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderOnboardingI9Step = () => {
    const fluCard = currentSection.cards.find((card) => card.title === 'Flu Declination');
    const benefitsCard = currentSection.cards.find((card) => card.title === 'Medical Benefits');
    const reviewDocumentsCard = currentSection.cards.find((card) => card.title === 'Review Documents');
    const signatureCard = currentSection.cards.find((card) => card.title === 'Signature');
    const fluFields = fluCard?.fields ?? [];
    const waivedBenefitsField = benefitsCard?.fields.find((field) => field.name === 'waivedBenefits');
    const initialsField = signatureCard?.fields.find((field) => field.name === 'applicantInitials');
    const benefitsLanguage = documentLanguageFromPrimaryLanguage(formData.primaryLanguage);

    return (
      <Box mt="25px">
        <FormSection title={t('I9Verification', 'I-9 Verification')} icon={iconFor('contact')} sx={{ mb: 2, mt: 3 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography>
                {t('I9Text1a', 'Every new hire is required by law to verify both identity and eligibility to work in the U.S. To do this, you will need to provide documents that support your employment eligibility. The I-9 verification process will also require your signature. For more information, see the ')}
                <a target="_blank" rel="noreferrer" href="https://www.uscis.gov/i-9">
                  {t('I9Text1Link', "Department of Homeland Security's article")}
                </a>
                {t('I9Text1b', ' on the provisions of the I-9.')}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography>
                {t('I9Text2', 'I am aware that federal law provides for imprisonment and/or fines for false statements or use of false documents in connection with the completion of this form.')}
              </Typography>
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title={t('FluDeclination', 'Flu Declination')} icon={iconFor('favorite')} sx={{ mb: 2, mt: 3 }}>
          <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            {fluFields.filter((field) => isFieldVisible(field, formData)).map((field) => (
              <Grid key={field.name} size={{ xs: 12, sm: 6 }}>
                {renderField({
                  ...field,
                  required: (field.name === 'fluDeclinationReason' && showFluDeclination)
                    || (field.name === 'fluDeclineOtherReason' && isFluOtherReasonSelected(formData.fluDeclinationReason)),
                })}
              </Grid>
            ))}
          </Grid>
        </FormSection>

        <FormSection title={t('MedicalBenefits', 'Medical Benefits')} icon={iconFor('health')} sx={{ mb: 2, mt: 3 }}>
          <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            {waivedBenefitsField && (
              <Grid size={{ xs: 12, sm: 6 }}>
                {renderField(waivedBenefitsField)}
              </Grid>
            )}
            <Grid size={{ xs: 0, sm: 1 }}>
              <Box />
            </Grid>
            {asString(formData.waivedBenefits) === 'No' && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box
                  onClick={() => void openTemplatePreview(t('MedicalBenefitFileTitle', 'Medical Benefit Enrollment Form'), 'HHA Benefits Waiver', benefitsLanguage)}
                  sx={{
                    cursor: 'pointer',
                    border: '1px solid #ddd',
                    borderRadius: 2,
                    p: 2,
                    textAlign: 'center',
                    '&:hover': {
                      boxShadow: '0px 4px 10px #330662',
                    },
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    {t('MedicalBenefitFileTitle', 'Medical Benefit Enrollment Form')}
                  </Typography>
                  <Typography variant="body2">
                    {t('MedicalBenefitFileText', 'Please click here to download this form and send the completed version to hr@linkhomecare.com')}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </FormSection>

        <FormSection title={t('ReviewDocuments', 'Review Documents')} icon={iconFor('description')} sx={{ mb: 2, mt: 3 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography>
                {reviewDocumentsCard?.description
                  ? translateText(reviewDocumentsCard.description)
                  : t('ReviewDocsText', 'Please click on the link to review each of the documents listed below. At the end of the onboarding process you will be prompted to sign, wherein you agree as the person identified, to all of the conditions listed in the documents.')}
              </Typography>
            </Grid>
          </Grid>
          {renderDocumentReviewTiles()}
        </FormSection>

        <Grid container alignItems="center" spacing={0} sx={{ mt: 2 }}>
          <Grid size="auto">
            <Typography variant="h5" sx={{ mr: 1, whiteSpace: 'nowrap' }}>
              {t('Signature', 'Signature')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            {initialsField && (
              <FormTextField
                inputRef={applicantInitialsInputRef}
                label={t('Applicant_Initials__c', initialsField.label)}
                value={asString(formData[initialsField.name])}
                onChange={(event) => updateValue(initialsField.name, event.target.value)}
                required={initialsField.required}
                error={Boolean(fieldErrors[initialsField.name])}
                helperText={fieldErrors[initialsField.name] || translateOptionalText(initialsField.helperText)}
                fullWidth
                sx={{ ml: 0 }}
                inputProps={{
                  maxLength: 3,
                  pattern: '[A-Za-z]*',
                }}
              />
            )}
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
          <Grid sx={{ mt: 1 }} size={12}>
            <Typography variant="body2">
              {signatureCard?.description
                ? translateText(signatureCard.description)
                : t('SignatureText', 'Electronic Signature Agreement: By signing this Acknowledgement, I agree that my electronic signature is the legally binding equivalent to my handwritten signature. Whenever I execute an electronic signature, it has the same validity and meaning as my handwritten signature. I will not, at any time in the future, repudiate the meaning of my electronic signature or claim that my electronic signature is not legally binding.')}
            </Typography>
          </Grid>
        </Grid>
        <Box className="textBarrierDiv" />
        <Grid container spacing={{ xs: 0, sm: 0 }}>
          <Grid size={12}>
            <Box
              sx={{
                border: signatureError ? '2px solid #d32f2f' : '1px solid #000000',
                width: '100%',
                height: 200,
                mb: 2,
              }}
            >
              <SignatureCanvas
                key={`sig-pad-${currentStep}`}
                ref={signaturePadRef}
                onEnd={() => void handleSignatureEnd()}
                canvasProps={{
                  height: 200,
                  className: 'signatureCanvas',
                }}
              />
            </Box>
            {signatureError && (
              <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>
                {signatureError}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderApplicationConsentStep = () => {
    const citizenStatusCard = currentSection.cards.find((card) => card.title === 'Citizen Status');
    const reviewFormsCard = currentSection.cards.find((card) => card.title === 'Review Forms');
    const signatureCard = currentSection.cards.find((card) => card.title === 'Signature');
    const citizenStatusField = citizenStatusCard?.fields.find((field) => field.name === 'citizenStatus');
    const initialsField = signatureCard?.fields.find((field) => field.name === 'applicantInitials');

    return (
      <>
        {citizenStatusField && (
          <FormSection
            title={translateText(citizenStatusCard?.title ?? 'Citizen Status')}
            icon={iconFor(citizenStatusCard?.icon)}
            sx={{ mt: 3, mb: 2 }}
          >
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t(
                'I9Text3',
                citizenStatusCard?.description
                  ?? 'I attest, under penalty of perjury, that I am (select one of the following):',
              )}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                {renderField(citizenStatusField)}
              </Grid>
            </Grid>
          </FormSection>
        )}

        <Typography variant="h5" sx={{ mt: 3 }}>
          {reviewFormsCard?.title ?? 'Review Forms'}
        </Typography>
        {reviewFormsCard?.description && (
          <Typography variant="body2">
            {reviewFormsCard.description}
          </Typography>
        )}
        {renderReviewFormTiles()}

        <Grid container alignItems="center" spacing={0} sx={{ mt: 4 }}>
          <Grid size="auto">
            <Typography variant="h5" sx={{ mr: 1, whiteSpace: 'nowrap' }}>
              {signatureCard?.title ?? 'Signature'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            {initialsField && (
              <FormTextField
                inputRef={applicantInitialsInputRef}
                label={t('Applicant_Initials__c', initialsField.label)}
                value={asString(formData[initialsField.name])}
                onChange={(event) => updateValue(initialsField.name, event.target.value)}
                required={initialsField.required}
                error={Boolean(fieldErrors[initialsField.name])}
                helperText={fieldErrors[initialsField.name] || initialsField.helperText}
                fullWidth
                sx={{ ml: 0 }}
                inputProps={{
                  maxLength: 3,
                  pattern: '[A-Za-z]*',
                }}
              />
            )}
          </Grid>
        </Grid>

        {signatureCard?.description && (
          <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
            <Grid sx={{ mt: 1 }} size={{ xs: 12, sm: 12 }}>
              <Typography variant="body2">
                {signatureCard.description}
              </Typography>
            </Grid>
          </Grid>
        )}
        <Box className="textBarrierDiv" />
        <Grid container spacing={{ xs: 0, sm: 0 }}>
          <Grid size={12}>
            <Box
              sx={{
                border: signatureError ? '2px solid #d32f2f' : '1px solid #000000',
                width: '100%',
                height: 200,
                mb: 2,
              }}
            >
              <SignatureCanvas
                key={`sig-pad-${currentStep}`}
                ref={signaturePadRef}
                onEnd={() => void handleSignatureEnd()}
                canvasProps={{
                  height: 200,
                  className: 'signatureCanvas',
                }}
              />
            </Box>
            {signatureError && (
              <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>
                {signatureError}
              </Typography>
            )}
          </Grid>
        </Grid>
      </>
    );
  };

  const handleBackClick = () => {
    if (isWorkflowLocked) return;
    if (isOnboardingWotcStep && currentWotcStep > 1) {
      setCurrentWotcStep((step) => Math.max(step - 1, 1));
      return;
    }
    if (isOnboardingYouthCreditStep && currentYouthCreditStep > 1) {
      setCurrentYouthCreditStep((step) => Math.max(step - 1, 1));
      return;
    }
    if (isOnboardingTbStep && currentTbStep > 1) {
      setCurrentTbStep((step) => Math.max(step - 1, 1));
      return;
    }
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  const handleNextClick = () => {
    if (isWorkflowLocked) return;
    if (isFinalEditableStep) {
      void reportClientEvent({
        level: 'info',
        context: 'Caregiver submit clicked',
        details: submitDebugDetails(),
      });
    }
    if (isOnboardingWotcStep && currentWotcStep < WOTC_QUESTIONS.length) {
      setCurrentWotcStep((step) => Math.min(step + 1, WOTC_QUESTIONS.length));
      return;
    }
    if (isOnboardingYouthCreditStep && currentYouthCreditStep < YOUTH_CREDIT_QUESTIONS.length) {
      setCurrentYouthCreditStep((step) => Math.min(step + 1, YOUTH_CREDIT_QUESTIONS.length));
      return;
    }
    if (isOnboardingTbStep && currentTbStep === 1) {
      if (validateFieldsByName(TB_RISK_QUESTIONS.map((question) => question.fieldName))) {
        setCurrentTbStep(2);
      }
      return;
    }
    void saveCurrentStep(true);
  };

  return (
    <Container id="c1" maxWidth="lg" sx={{ mt: 5, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 1,
          mb: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'rgba(255,255,255,1.00)',
          pr: { xs: '16px', sm: 1 },
          pl: { xs: '3px', sm: 1 },
        }}
      >
        <Box sx={{ mt: 2, p: 0.5, width: '100%' }}>
          <NavStatusBar
            steps={translatedSections}
            currentStep={currentStep}
            onStepClick={handleStepClick}
            disabled={saving || isWorkflowLocked}
          />

          <Box sx={{ m: { xs: 0.5, sm: 1 }, ml: { xs: 1 }, width: { xs: '100%', sm: 'auto' } }}>
            {isSubmittedStatus ? renderSubmittedStep() : isApplicationSubmitStep ? renderApplicationConsentStep() : isOnboardingTaxStep ? renderTaxFormsStep() : isOnboardingWotcStep ? renderWotcStep() : isOnboardingYouthCreditStep ? renderYouthCreditStep() : isOnboardingPreferencesStep ? renderPreferencesStep() : isOnboardingTbStep ? renderTbAssessmentStep() : isOnboardingI9Step ? renderOnboardingI9Step() : currentSection.cards.map((card, cardIndex) => (
              <FormSection
                key={`${currentSection.status}-${card.title}`}
                title={translateText(card.title)}
                icon={iconFor(card.icon)}
                sx={cardIndex === 0 ? { mt: 3 } : undefined}
              >
                {card.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: card.fields.length ? 2 : 0 }}>
                    {translateText(card.description)}
                  </Typography>
                )}
                {card.title === 'Address'
                  && ((kind === 'application' && currentSection.status === 'New')
                    || (kind === 'onboarding' && currentSection.status === 'Onboarding')) && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={12}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'rgba(112, 43, 157, 0.28)',
                          borderRadius: 2,
                          bgcolor: 'rgba(112, 43, 157, 0.04)',
                          p: { xs: 1.5, sm: 2 },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ color: '#4f1f70', fontWeight: 700 }}>
                            {t('address.lookupTitle', 'Address lookup')}
                          </Typography>
                          <Chip
                            label={t('address.optional', 'Optional')}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, color: '#6a2a91', borderColor: 'rgba(112, 43, 157, 0.35)' }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {t(
                            'address.lookupInstructions',
                            'Choose a suggestion to fill the address fields below, or skip this and enter them manually.'
                          )}
                        </Typography>
                        <AddressAutocomplete
                          label={t('address.lookupLabel', 'Search for an address')}
                          selectedHelperText={t('address.lookupSelected', 'Address fields filled — review or edit them below.')}
                          onAddressSelect={updateAddress}
                        />
                      </Box>
                    </Grid>
                    <Grid size={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography
                          variant="overline"
                          sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1 }}
                        >
                          {t('address.detailsTitle', 'Address details')}
                        </Typography>
                        <Box sx={{ flex: 1, borderTop: '1px solid', borderColor: 'divider' }} />
                      </Box>
                    </Grid>
                  </Grid>
                )}
                {card.fields.some((field) => isFieldVisible(field, formData)) && (
                  <Grid container spacing={2}>
                    {card.fields.filter((field) => isFieldVisible(field, formData)).map((field) => (
                      <Grid
                        key={field.name}
                        size={
                          field.width === 'full'
                            ? 12
                            : {
                              xs: 12,
                              sm: field.width === 'third' ? 4 : field.width === 'quarter' ? 3 : 6,
                            }
                        }
                      >
                        {renderField(field)}
                      </Grid>
                    ))}
                  </Grid>
                )}
              </FormSection>
            ))}
          </Box>

          {!isSubmittedStatus && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              {hasSignatureField && (
                <Box sx={{ mr: 2 }}>
                  <Button variant="outlined" color="secondary" disabled={saving} onClick={clearSignature}>
                    {t('ClearSignature', 'Clear Signature')}
                  </Button>
                </Box>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {currentStep > 0 && (
                  <Button disabled={saving} onClick={handleBackClick} sx={{ mr: 1 }}>
                    {t('Back', 'Back')}
                  </Button>
                )}
                <Button variant="contained" disabled={saving} onClick={handleNextClick}>
                  {saving ? t('Saving', 'Saving...') : isFinalEditableStep ? t('Submit', 'Submit') : t('Next', 'Next')}
                </Button>
              </Box>
            </Box>
          )}

        </Box>
        <Dialog
          open={Boolean(templateDialog)}
          onClose={() => setTemplateDialog(null)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle
            sx={{
              bgcolor: '#330662',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="h6">{templateDialog?.title}</Typography>
            <Button
              variant="contained"
              color="inherit"
              onClick={() => void handleTemplateDownload()}
              disabled={!templateDialog || templateDialog.loading}
              sx={{ color: '#330662' }}
            >
              {t('documents.downloadPdf', 'Download PDF')}
            </Button>
          </DialogTitle>
          <DialogContent dividers sx={{ bgcolor: '#f5f5f5', minHeight: 500 }}>
            {templateDialog?.loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            )}
            {templateDialog?.error && <Alert severity="error">{templateDialog.error}</Alert>}
            {templateDialog?.url && (
              <Box
                component="iframe"
                src={templateDialog.url}
                title={`${templateDialog.title} PDF`}
                sx={{ width: '100%', height: 650, border: 0, bgcolor: 'white' }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTemplateDialog(null)}>{t('Close', 'Close')}</Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={8000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading || saving}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Paper>
    </Container>
  );
}
